import { prepareNewLotStateCommand } from './newLotStateCommand.js';
import { withMutationWatchdog } from './mutationWatchdog.js';
import { orderMastersByName } from './masterNameOrder.js';
import { ACTIVE_CLEANING_EVENTS_FILTER_SQL } from './cleaningEvents.js';
import { toUserMessage } from '../errors.js';
import { useMemo, useRef } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EventKind,
  MasterOp,
  StockCommandMasterEffectV1,
  StockCommandV1,
  StockProjectionOptions,
  StockProjectionReservation,
  StockProjectorEvent,
  SyncEventInput,
} from '@trackeat/shared';
import {
  CollettivaPrelabelListSchema,
  projectStock,
  type CollettivaPrelabelRow,
} from '@trackeat/shared';
import { CollettivaDischargeDraftSchema, type CollettivaDischargeDraft } from '@trackeat/shared';
import { openLocalDb, getMeta, flushLocalDb } from '../db/local.js';
import { getSyncEngine } from '../sync/engine.js';
import type { EnqueuedStockCommandHandle } from '../sync/stockCommandOutbox.js';
import { useAuth } from '../auth/AuthProvider.js';
import {
  ActiveTenantAccountContextSchema,
  type AccountAccessMode,
  type ActiveTenantAccountContext,
} from '../auth/accountAccess.js';
import { getSupabase } from '../supabase.js';
import { dbg } from '../debug/debugLog.js';
import { beginNativePerfRead, finishNativePerfSpan, invokeNativePerfRead, markNativePerfRead } from '../debug/nativePerfTrace.js';
import {
  activeBusinessIdsForTenant,
  shouldIncludeLegacyUnscopedBusiness,
  type StockProjectionBusinessMaster,
} from './stockProjectionBusinessScope.js';
import { shipmentLineReservesStockV1 } from '../shipmentStockPolicy.js';
import { resolveTransportDocumentsAccessState } from './transportDocumentsAccess.js';

// Diagnostica "collezione vuota": serve a scoprire bug di scoping/cache-miss,
// NON a segnalare lo stato normale (una tabella legittimamente vuota è la
// regola, non l'eccezione). Storicamente questo emetteva un `warn` ad OGNI
// render con 0 righe → ~134k warn/settimana di puro rumore + un COUNT(*) extra
// ogni volta. Ora: si emette al massimo UNA volta per scope (tabella/kind +
// tenant) per sessione, a livello `info` (campionato dal flusher, fuori dal
// segnale error/warn). `emptyDiagSeen` si svuota a ogni reload dell'app.
const emptyDiagSeen = new Set<string>();
function shouldEmitEmptyDiag(key: string): boolean {
  if (emptyDiagSeen.has(key)) return false;
  emptyDiagSeen.add(key);
  return true;
}

export interface TenantMember {
  user_id: string;
  full_name: string;
  role: string;
}

/**
 * Reads cached master rows for the given table, scoped to the active tenant.
 * Archived rows are excluded unless a recovery/read-model surface explicitly
 * asks for them. Operational pickers must keep the default fail-closed view.
 */
export function useMasters<T = Record<string, unknown>>(
  table: string,
  options: { includeArchived?: boolean } = {},
) {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const includeArchived = options.includeArchived === true;

  return useQuery<T[]>({
    queryKey: ['masters', table, tenantId, { includeArchived }],
    networkMode: 'always', // SQLite reads must also run after an offline cold start.
    enabled: !!tenantId,
    queryFn: async () => {
      const trace = beginNativePerfRead(table === 'foods' && includeArchived ? 'master:foods:all' : `master:${table}`);
      try {
        const t0 = performance.now();
        let db;
        try {
          db = await openLocalDb(null);
        } catch (err) {
          dbg.error('useMasters', `${table}_db_open_failed`, {
            tenant_id: tenantId,
            error: toUserMessage(err),
          });
          throw err;
        }
        markNativePerfRead(trace, 'db_open');
        const res = await invokeNativePerfRead(trace, () => db.query(
          `SELECT row FROM masters_local
           WHERE table_name = ?${includeArchived ? '' : ' AND archived = 0'}
             AND (tenant_id = ? OR tenant_id IS NULL)`,
          [table, tenantId],
        ));
        markNativePerfRead(trace, 'query');
        const rows = (res.values ?? []) as { row: string }[];
        const ms = Math.round(performance.now() - t0);
        // Diagnostica "vuoto" una sola volta per (tabella, tenant) per sessione,
        // a `info`. Il COUNT(*) di confronto cross-tenant gira solo in quella
        // unica occasione, non a ogni render.
        if (rows.length === 0 && shouldEmitEmptyDiag(`masters:${table}:${tenantId}`)) {
          // Counter di sicurezza: confronta con riga totale (anche di altri
          // tenant) per capire se il problema è scoping o assenza vera.
          let totalAny = 0;
          try {
            const all = await db.query(
              `SELECT COUNT(*) as n FROM masters_local WHERE table_name = ?`,
              [table],
            );
            totalAny = ((all.values?.[0] as { n: number } | undefined)?.n) ?? 0;
          } catch { /* noop */ }
          dbg.info('useMasters', `${table}_empty`, {
            tenant_id: tenantId,
            ms,
            total_any_tenant: totalAny,
          });
        }
        markNativePerfRead(trace, 'empty_diagnostics');
        const result = orderMastersByName(table, rows.map((r) => JSON.parse(r.row) as T));
        markNativePerfRead(trace, 'parse_sort');
        finishNativePerfSpan(trace, 'ok');
        return result;
      } catch (error) {
        finishNativePerfSpan(trace, 'error');
        throw error;
      }
    },
  });
}

export function useEvents(kind?: EventKind, limit: number | null = 100, includeVoided = false) {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  return useQuery({
    queryKey: ['events', kind ?? '*', tenantId, limit, includeVoided],
    networkMode: 'always',
    enabled: !!tenantId,
    queryFn: async () => {
      const trace = beginNativePerfRead(limit === null ? `event:${kind ?? ''}` : '');
      try {
        const t0 = performance.now();
        const db = await openLocalDb(null);
        markNativePerfRead(trace, 'db_open');
        // T-098: `limit === null` → nessuna clausola LIMIT, tutti gli eventi del
        // tenant. Va usato sulle feature operative del ristoratore (storico
        // etichette, registro HACCP) dove un cap rischia di tagliargli silenziosamente
        // lo storico. Per le dashboard/widget "ultimi N" passare un numero esplicito.
        // Memory: feedback_no_app_side_limits_for_user_features.
        // Accettiamo anche tenant_id IS NULL come fallback (consistency con useMasters):
        // events scritti dal writeback diretto possono avere tenant_id presente, ma
        // tutela vs eventuale bug futuro che ne dimentica la valorizzazione.
        const useLimit = limit !== null;
        const baseWhere = kind
          ? `WHERE (tenant_id = ? OR tenant_id IS NULL) AND kind = ?`
          : `WHERE (tenant_id = ? OR tenant_id IS NULL)`;
        const sql = `SELECT id, server_id, tenant_id, kind, business_id, client_ts, server_ts, rev, payload
               FROM events_local ${baseWhere}${kind === 'cleaning_done' && !includeVoided ? ACTIVE_CLEANING_EVENTS_FILTER_SQL : ''}
               ORDER BY rev DESC NULLS LAST, client_ts DESC${useLimit ? ' LIMIT ?' : ''}`;
        const baseParams = kind ? [tenantId, kind] : [tenantId];
        const params = useLimit ? [...baseParams, limit] : baseParams;
        const res = await invokeNativePerfRead(trace, () => db.query(sql, params));
        markNativePerfRead(trace, 'query');
        const rows = (res.values ?? []).map((r) => {
          const row = r as Record<string, unknown>;
          return { ...row, payload: JSON.parse(row.payload as string) as Record<string, unknown> };
        });
        markNativePerfRead(trace, 'parse_sort');
        if (rows.length === 0 && shouldEmitEmptyDiag(`events:${kind ?? 'all'}:${tenantId}`)) {
          let totalAny = 0;
          let totalKind = 0;
          let tenantsPresent: Array<{ tid: string | null; n: number }> = [];
          try {
            const all = await db.query(`SELECT COUNT(*) as n FROM events_local`);
            totalAny = ((all.values?.[0] as { n: number } | undefined)?.n) ?? 0;
            if (kind) {
              const ofKind = await db.query(
                `SELECT COUNT(*) as n FROM events_local WHERE kind = ?`,
                [kind],
              );
              totalKind = ((ofKind.values?.[0] as { n: number } | undefined)?.n) ?? 0;
            }
            // Mappa: ogni tenant_id distinto presente + count
            const distinct = await db.query(
              `SELECT tenant_id, COUNT(*) as n FROM events_local GROUP BY tenant_id`,
            );
            tenantsPresent = ((distinct.values ?? []) as Array<{ tenant_id: string | null; n: number }>)
              .map((r) => ({ tid: r.tenant_id, n: r.n }));
          } catch { /* noop */ }
          dbg.info('useEvents', `${kind ?? 'all'}_empty`, {
            tenant_id: tenantId,
            ms: Math.round(performance.now() - t0),
            total_any_tenant: totalAny,
            total_of_kind_any_tenant: totalKind,
            tenants_present: tenantsPresent,
          });
        }
        markNativePerfRead(trace, 'empty_diagnostics');
        finishNativePerfSpan(trace, 'ok');
        return rows;
      } catch (error) {
        finishNativePerfSpan(trace, 'error');
        throw error;
      }
    },
  });
}

export interface StockProjectionQueryOptions {
  canonicalUomByFood?: StockProjectionOptions['canonicalUomByFood'];
  includeStorageUnit?: boolean;
  includeLegacyUnscopedBusiness?: boolean;
}

interface DraftShipmentReservationMaster {
  id: string;
  business_id?: string | null;
  status?: string | null;
  archived_at?: string | null;
  lines?: Array<{
    food_id?: string | null;
    production_id?: string | null;
    stock_effect_v1?: 'dispatch' | 'none' | string | null;
    batch_lot?: string | null;
    storage_unit_id?: string | null;
    qty?: number | string | null;
    uom?: string | null;
  }> | null;
}

/**
 * Canonical, uncapped stock read model for the active tenant/business scope.
 *
 * The three event streams intentionally use `limit=null`: truncating any one
 * of them would make the materialized balance depend on an arbitrary history
 * window. Draft shipment masters are folded in as external reservations so
 * every stock consumer sees the same available quantity. Invalid or
 * incompatible movements remain available in `data.quarantine` and are never
 * folded into a balance.
 */
export function useStockProjection(options: StockProjectionQueryOptions = {}) {
  const { profile } = useAuth();
  const collettivaEnabled = useCollettivaEnabled();
  const tenantId = profile?.active_tenant_id ?? null;
  const businessId = profile?.active_business_id ?? null;
  const stockIn = useEvents('stock_in', null);
  const stockOut = useEvents('stock_out', null);
  const stockReserved = useEvents('stock_reserved', null);
  const draftShipments = useMasters<DraftShipmentReservationMaster>('shipments');
  const tenantBusinesses = useMasters<StockProjectionBusinessMaster>('businesses');
  const activeBusinessIds = useMemo(
    () => activeBusinessIdsForTenant(tenantBusinesses.data ?? [], tenantId),
    [tenantBusinesses.data, tenantId],
  );
  const includeLegacyUnscopedBusiness =
    options.includeLegacyUnscopedBusiness
    // A pre-business event may safely belong to the sole site. Once a tenant
    // has multiple sites, replaying the same null-business movement in each
    // warehouse would duplicate stock, so the implicit fallback is disabled.
    ?? shouldIncludeLegacyUnscopedBusiness({
      activeBusinessIds,
      activeBusinessId: businessId,
      isLoading: tenantBusinesses.isLoading,
      isError: tenantBusinesses.isError,
    });
  const externalReservations = useMemo<StockProjectionReservation[]>(() => {
    if (!tenantId) return [];
    const reservations: StockProjectionReservation[] = [];
    for (const shipment of draftShipments.data ?? []) {
      if (shipment.status !== 'draft' || shipment.archived_at) continue;
      for (const [lineIndex, line] of (shipment.lines ?? []).entries()) {
        if (!shipmentLineReservesStockV1(line, collettivaEnabled)) continue;
        const foodId = line.food_id?.trim();
        const batchLot = line.batch_lot?.trim();
        // Existing shipment semantics reserve a concrete lot. Lines without a
        // lot are intentionally ignored until the operator selects one.
        if (!foodId || !batchLot) continue;
        reservations.push({
          id: `${shipment.id}:${lineIndex}`,
          tenantId,
          businessId: shipment.business_id ?? null,
          foodId,
          batchLot,
          storageUnitId: line.storage_unit_id?.trim() || null,
          qty: Number(line.qty),
          // Legacy drafts may omit the UoM. The food default is the only safe
          // fallback; an empty value is rejected fail-closed by the projector.
          uom: line.uom?.trim() || options.canonicalUomByFood?.[foodId] || '',
        });
      }
    }
    return reservations;
  }, [
    collettivaEnabled,
    draftShipments.data,
    options.canonicalUomByFood,
    tenantId,
  ]);

  const data = useMemo(() => projectStock(
    [
      ...(stockIn.data ?? []),
      ...(stockOut.data ?? []),
      ...(stockReserved.data ?? []),
    ] as StockProjectorEvent[],
    {
      scope: { tenantId: tenantId ?? '', businessId },
      canonicalUomByFood: options.canonicalUomByFood,
      includeStorageUnit: options.includeStorageUnit,
      includeLegacyUnscopedBusiness,
      externalReservations,
    },
  ), [
    businessId,
    externalReservations,
    includeLegacyUnscopedBusiness,
    options.canonicalUomByFood,
    options.includeStorageUnit,
    stockIn.data,
    stockOut.data,
    stockReserved.data,
    tenantId,
  ]);

  return {
    data,
    isLoading:
      stockIn.isLoading
      || stockOut.isLoading
      || stockReserved.isLoading
      || draftShipments.isLoading
      || tenantBusinesses.isLoading,
    isError:
      stockIn.isError
      || stockOut.isError
      || stockReserved.isError
      || draftShipments.isError
      || tenantBusinesses.isError,
    error:
      stockIn.error
      ?? stockOut.error
      ?? stockReserved.error
      ?? draftShipments.error
      ?? tenantBusinesses.error,
  };
}

export interface FiscalDocMeta {
  id: string;
  number: string | null;
  date: string | null;
  supplier_id: string | null;
  type: string | null;
}

/**
 * Metadati (numero/data/fornitore/tipo) delle fatture dell'archivio fiscale del
 * tenant ATTIVO. Online, con filtro ESPLICITO su `active_tenant_id` (oltre alla
 * RLS) → cassetti separati per ristorante. Le fatture NON sono nel master locale
 * `documents` (che contiene solo i DDT): la tracciabilità usa questo hook per
 * mostrare numero/fornitore dei carichi che provengono da una fattura. Degrada
 * a vuoto offline (i DDT restano dal master locale).
 */
export function useFiscalDocs() {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  return useQuery<FiscalDocMeta[]>({
    queryKey: ['fiscal_docs_meta', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('fiscal_documents')
        .select('id,number,date,supplier_id,type')
        .eq('tenant_id', tenantId);
      if (error) throw error;
      return (data ?? []) as FiscalDocMeta[];
    },
  });
}

/**
 * Lists members of the active tenant. Online-only — used to populate
 * assignment selectors in cleaning schedules and similar forms.
 */
export function useTenantMembers() {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  return useQuery<TenantMember[]>({
    queryKey: ['tenant_members', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenant_members')
        .select('user_id, role, user_profiles!inner(full_name)')
        .eq('tenant_id', tenantId);
      if (error) throw error;
      type Row = { user_id: string; role: string; user_profiles: { full_name: string } | { full_name: string }[] };
      return ((data ?? []) as Row[]).map((r) => {
        const prof = Array.isArray(r.user_profiles) ? r.user_profiles[0] : r.user_profiles;
        return {
          user_id: r.user_id,
          full_name: prof?.full_name ?? r.user_id.slice(0, 8),
          role: r.role,
        };
      });
    },
    staleTime: 60_000,
  });
}

export interface TenantAccount {
  user_id: string;
  full_name: string;
  email: string;
  app_role: string; // ristoratore | osa | consulente | staff | preposto
  access_mode: AccountAccessMode;
  kind: 'member' | 'preposto';
  destination_id: string | null;
  destination_name: string | null;
}

const ACCOUNT_CONTEXT_CACHE_PREFIX = 'trackeat.account_context_v1.';

function accountContextCacheKey(tenantId: string, userId: string): string {
  return `${ACCOUNT_CONTEXT_CACHE_PREFIX}${tenantId}.${userId}`;
}

function readCachedAccountContext(
  tenantId: string | null,
  userId: string | null,
): ActiveTenantAccountContext | undefined {
  if (!tenantId || !userId) return undefined;
  try {
    const raw = localStorage.getItem(accountContextCacheKey(tenantId, userId));
    if (!raw) return undefined;
    const parsed = ActiveTenantAccountContextSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedAccountContext(context: ActiveTenantAccountContext): void {
  try {
    localStorage.setItem(
      accountContextCacheKey(context.tenant_id, context.user_id),
      JSON.stringify(context),
    );
  } catch { /* noop */ }
}

/**
 * Modalita del login nel tenant attivo. E separata dal ruolo JWT: un tablet
 * condiviso rimane `staff`, ma usa una chiave dashboard dedicata. La cache
 * permette il cold start offline; senza server e senza cache fallisce chiuso.
 */
export function useAccountAccessState(): {
  context: ActiveTenantAccountContext | null;
  visibilitySubject: string;
  isSharedTablet: boolean;
  resolved: boolean;
  refreshing: boolean;
  unavailable: boolean;
} {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const userId = profile?.id ?? null;
  const cached = readCachedAccountContext(tenantId, userId);
  const query = useQuery<ActiveTenantAccountContext>({
    queryKey: ['active_tenant_account_context', tenantId, userId],
    enabled: !!tenantId && !!userId,
    initialData: cached,
    initialDataUpdatedAt: cached === undefined ? undefined : 0,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      // React Query owns retries here. Stacking PostgREST retries delays the
      // existing offline snapshot fallback for seconds on a reachable 503.
      const { data, error } = await getSupabase().rpc('active_tenant_account_context_v1').retry(false);
      if (error) throw error;
      const context = ActiveTenantAccountContextSchema.parse(data);
      if (context.tenant_id !== tenantId || context.user_id !== userId) {
        throw new Error('account_context_scope_mismatch');
      }
      writeCachedAccountContext(context);
      return context;
    },
  });
  const context = query.data ?? cached ?? null;
  const offlineSnapshotResolved = cached !== undefined
    && (!navigator.onLine || query.isError);
  const resolved = !!tenantId && !!userId && (query.dataUpdatedAt > 0 || offlineSnapshotResolved);
  return {
    context,
    visibilitySubject: context?.visibility_subject ?? profile?.role ?? 'staff',
    isSharedTablet: context?.access_mode === 'shared_tablet',
    resolved,
    refreshing: query.isFetching,
    unavailable: !!tenantId && !!userId && query.isError && !offlineSnapshotResolved,
  };
}

/**
 * T-306: elenco degli account (login) del locale attivo con email + permesso
 * app, per la sezione "Account & permessi" in Personale. Online-only — l'email
 * vive solo in auth.users, quindi via RPC SECURITY DEFINER `tenant_accounts_list`
 * gateato sul JWT (solo ristoratore/osa/consulente del locale; staff/preposto/
 * cross-tenant → 403). Include anche i preposti esterni con la loro destinazione.
 * `enabled` va passato = isAdmin per non scatenare un 403 sugli utenti non-admin.
 */
export function useTenantAccounts(enabled = true) {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  return useQuery<TenantAccount[]>({
    queryKey: ['tenant_accounts', tenantId],
    enabled: enabled && !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase().rpc('tenant_accounts_list', { p_tenant_id: tenantId });
      if (error) throw error;
      return (data ?? []) as TenantAccount[];
    },
  });
}

/**
 * Fetches name/status for every tenant the user belongs to (profile.tenants[]).
 * Used by Settings to show the "Azienda" switcher with real names instead of
 * UUID prefixes (T-062). Caches the id→name map in localStorage so the cold
 * start doesn't flash "94f588eb" before the network resolves.
 */
const TENANT_NAMES_CACHE_KEY = 'trackeat.tenant_names_cache_v1';

function readTenantNameCache(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TENANT_NAMES_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, string>;
  } catch { return {}; }
}

function writeTenantNameCache(map: Record<string, string>): void {
  try { localStorage.setItem(TENANT_NAMES_CACHE_KEY, JSON.stringify(map)); } catch { /* noop */ }
}

export interface UserTenantRow { id: string; name: string }

export interface UserBusinessRow {
  id: string;
  tenant_id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  provincia: string | null;
}

export function useUserTenants() {
  const { profile } = useAuth();
  const ids = profile?.tenants ?? [];
  const key = ids.slice().sort().join(',');
  return useQuery<UserTenantRow[]>({
    queryKey: ['user_tenants', key],
    enabled: ids.length > 0,
    // Niente staleTime grande: vogliamo refetch ogni montaggio per essere sicuri
    // che le rinominazioni / nuovi tenant arrivino senza dover ricaricare l'app.
    staleTime: 0,
    refetchOnMount: 'always',
    // Niente initialData: React Query v5 con initialData senza
    // `initialDataUpdatedAt` può marcarla come fresca-recente e NON refetchare,
    // lasciando i nomi vuoti per sempre. Usiamo `placeholderData` invece, che
    // viene mostrato durante il loading ma non blocca il fetch.
    placeholderData: ids.map((id) => {
      const cache = readTenantNameCache();
      return { id, name: cache[id] ?? '' };
    }),
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('id,name')
        .in('id', ids);
      if (error) throw error;
      const rows = (data ?? []) as UserTenantRow[];
      // Cache: merge nuovi nomi mantenendo eventuali vecchi (per tenant non
      // ancora caricati o appena perduti per via di un'invalidazione di RLS).
      const cache = readTenantNameCache();
      for (const r of rows) cache[r.id] = r.name;
      writeTenantNameCache(cache);
      // Ritorna sempre nell'ordine di `ids` così la UI è stabile.
      const byId = new Map(rows.map((r) => [r.id, r] as const));
      return ids.map((id) => byId.get(id) ?? { id, name: cache[id] ?? '' });
    },
  });
}

/**
 * Directory online delle sedi appartenenti ai tenant presenti nel JWT.
 * La query resta ulteriormente limitata dagli ID della membership e dalla RLS;
 * viene attivata solo nelle superfici consulente/owner che ne hanno bisogno.
 */
export function useUserBusinesses(enabled = true) {
  const { profile } = useAuth();
  const ids = profile?.tenants ?? [];
  const key = ids.slice().sort().join(',');
  return useQuery<UserBusinessRow[]>({
    queryKey: ['user_businesses', key],
    enabled: enabled && ids.length > 0,
    staleTime: 60_000,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data, error } = await getSupabase().rpc('consultant_business_directory_v1');
      if (error) throw error;
      return (data ?? []) as UserBusinessRow[];
    },
  });
}

export interface EnqueueEventInput {
  kind: EventKind;
  business_id?: string | null;
  payload: Record<string, unknown>;
  clientUuid?: string;
  // T-333 (recupero postumo): timestamp dell'operazione, retrodatato al giorno
  // reale + ora dichiarata dall'operatore. Default = now() (registrazione
  // odierna). NB: `client_ts` NON entra nella hash chain (prev_hash|kind|payload)
  // → retrodatarlo non rompe la catena; `server_ts` resta sempre now() reale.
  clientTs?: string;
  // I controlli con prova fotografica coordinano esplicitamente upload e push:
  // l'evento locale viene scritto subito, ma il relativo hook avvia la sync
  // soltanto dopo aver tentato l'upload del byte richiesto dal gate server.
  deferBackgroundSync?: boolean;
}

export function useEnqueueEvent() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    networkMode: 'always', // Persist locally now; the sync engine owns network retries.
    mutationFn: async (input: EnqueueEventInput): Promise<void> => withMutationWatchdog('event', async () => {
      if (!profile?.active_tenant_id) throw new Error('Nessun tenant attivo');
      const deviceId = (await getMeta('device_id')) ?? 'unknown';
      const ev: SyncEventInput = {
        client_uuid: input.clientUuid ?? crypto.randomUUID(),
        kind: input.kind,
        tenant_id: profile.active_tenant_id,
        business_id: input.business_id ?? profile.active_business_id ?? null,
        device_id: deviceId,
        client_ts: input.clientTs ?? new Date().toISOString(),
        payload: input.payload,
      };
      await getSyncEngine().queueEvent(ev);
      await flushLocalDb();
      // Sync in background: NON awaitato perché il tick comporta round-trip
      // server (push + pull, anche oltre 5-10s su rete lenta) che renderebbe
      // la mutation lenta vanificando l'offline-first. La UI è già a posto:
      // queueEvent ha scritto ottimisticamente in events_local, e onSuccess
      // sotto invalida le query → useEvents rilegge dal locale e mostra il
      // nuovo evento istantaneamente. Il sync remoto avviene dopo.
      if (navigator.onLine && !input.deferBackgroundSync) {
        void getSyncEngine().tick().catch(() => undefined);
      }
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['masters'] });
    },
  });
}

/**
 * Single mobile entry point for versioned stock commands. The engine owns the
 * atomic outbox/optimistic event write; this hook only supplies the same local
 * flush, background sync and cache invalidation contract as `useEnqueueEvent`.
 */
export function useEnqueueStockCommand() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (
      input: StockCommandV1 | {
        command: StockCommandV1;
        master_effects: readonly StockCommandMasterEffectV1[];
      },
    ): Promise<EnqueuedStockCommandHandle> => withMutationWatchdog('stock command', async () => {
      const commandInput = 'command' in input ? input.command : input;
      const masterEffects = 'command' in input ? input.master_effects : [];
      // Compound label registrations must keep their cross-effect references exact.
      const command = masterEffects.length === 0
        ? await prepareNewLotStateCommand(commandInput)
        : commandInput;
      const handle = await getSyncEngine().queueStockCommand(
        command,
        masterEffects,
      );
      await flushLocalDb();
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
      return handle;
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['masters'] });
    },
  });
}

export function useEnqueueStockReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (
      input: Parameters<
        ReturnType<typeof getSyncEngine>['queueStockReconciliation']
      >[0],
    ) => withMutationWatchdog('stock reconciliation', async () => {
      const handle = await getSyncEngine().queueStockReconciliation(input);
      await flushLocalDb();
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
      return handle;
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['masters'] });
    },
  });
}

export function useEnqueueFoodSupplierMatchArchive() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (
      input: Parameters<
        ReturnType<typeof getSyncEngine>['queueFoodSupplierMatchArchive']
      >[0],
    ) => withMutationWatchdog('food supplier match archive', async () => {
      const handle = await getSyncEngine().queueFoodSupplierMatchArchive(input);
      await flushLocalDb();
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
      return handle;
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['masters'] });
    },
  });
}

export function useEnqueueSupplierItemMatchManagement() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (
      input: Parameters<
        ReturnType<typeof getSyncEngine>['queueSupplierItemMatchManagement']
      >[0],
    ) => withMutationWatchdog('supplier item match management', async () => {
      const handle = await getSyncEngine().queueSupplierItemMatchManagement(input);
      await flushLocalDb();
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
      return handle;
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['supplier-item-aliases'] });
    },
  });
}

/**
 * The historical-lot recovery uses its own guarded RPC, but keeps the same
 * durable/offline contract as every other stock operation.
 */
export function useEnqueueConsultantHistoricalLotRecount() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (
      input: Parameters<
        ReturnType<typeof getSyncEngine>['queueConsultantHistoricalLotRecount']
      >[0],
    ) => withMutationWatchdog('consultant historical lot recount', async () => {
      const handle = await getSyncEngine().queueConsultantHistoricalLotRecount(input);
      await flushLocalDb();
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
      return handle;
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['masters'] });
    },
  });
}

export interface EnqueueMasterInput extends Omit<MasterOp, 'device_id' | 'client_ts'> {}

export function useEnqueueMaster() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (input: EnqueueMasterInput): Promise<void> => withMutationWatchdog('master', async () => {
      const deviceId = (await getMeta('device_id')) ?? 'unknown';
      const op: MasterOp = {
        ...input,
        device_id: deviceId,
        client_ts: new Date().toISOString(),
      };
      await getSyncEngine().queueMaster(op);
      await flushLocalDb();
      // Sync remoto in background, non bloccante (vedi commento in useEnqueueEvent).
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['masters'] });
    },
  });
}

const WAREHOUSE_FLAG_CACHE_PREFIX = 'niveo.tenant_warehouse_enabled.';

// Ritorna undefined su cache-miss (non false): così l'hook NON passa un
// initialData "fresco" a React Query, che altrimenti — con staleTime>0 —
// salterebbe la prima fetch e mostrerebbe il flag OFF per tutta la staleTime
// (bug primo login su device nuovo). undefined ⇒ fetch immediata.
function readCachedWarehouseFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(WAREHOUSE_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedWarehouseFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(WAREHOUSE_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}

/**
 * Reads the per-tenant `warehouse_enabled` flag with a localStorage fallback so
 * that the UI is correct on cold start / offline. Refreshes from server when online.
 * Default false = base plan (warehouse hidden). True = Pro plan.
 */
export function useWarehouseEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedWarehouseFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_warehouse_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    initialDataUpdatedAt: cached === undefined ? undefined : 0,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('warehouse_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { warehouse_enabled: boolean } | null)?.warehouse_enabled;
      writeCachedWarehouseFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

// T-325: provider AI configurato per il tenant attivo (tenant_ai_config.
// default_provider). Letto in sola lettura dalla Home per mostrare l'etichetta
// del modello ("TrackEAT IA" / "TrackEAT IA PRO" / "Altro - X" via aiModelLabel).
const AI_PROVIDER_CACHE_PREFIX = 'niveo.tenant_ai_provider.';
function readCachedAiProvider(tenantId: string | null): string | undefined {
  if (!tenantId) return undefined;
  try {
    return localStorage.getItem(AI_PROVIDER_CACHE_PREFIX + tenantId) ?? undefined;
  } catch {
    return undefined;
  }
}
export function useTenantAiProvider(): string | null {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedAiProvider(tenantId);
  const query = useQuery<string>({
    queryKey: ['tenant_ai_provider', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenant_ai_config')
        .select('default_provider')
        .eq('tenant_id', tenantId!)
        .maybeSingle();
      if (error) throw error;
      const p = (data as { default_provider?: string | null } | null)?.default_provider ?? '';
      try { localStorage.setItem(AI_PROVIDER_CACHE_PREFIX + tenantId!, p); } catch { /* noop */ }
      return p;
    },
  });
  return query.data ?? cached ?? null;
}

export function useSetWarehouseEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      // T-130 (invariante I1): attivare il magazzino forza recipe_quantities_enabled
      // = true (auto-promote atomico). Disattivarlo NON spegne le quantità.
      const patch = enabled
        ? { warehouse_enabled: true, recipe_quantities_enabled: true }
        : { warehouse_enabled: false };
      const { error } = await getSupabase()
        .from('tenants')
        .update(patch)
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedWarehouseFlag(tenantId, enabled);
      if (enabled) writeCachedRecipeQtyFlag(tenantId, true);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_warehouse_enabled'] });
      void qc.invalidateQueries({ queryKey: ['tenant_recipe_quantities_enabled'] });
    },
  });
}

// ───────── T-130: flag "gestisci quantità nelle ricette" ─────────
const RECIPE_QTY_FLAG_CACHE_PREFIX = 'niveo.tenant_recipe_quantities_enabled.';

// undefined su miss ⇒ fetch immediata (come gli altri flag modulo). Ritornare
// `false` su miss + staleTime>0 spegneva il flag per 5 min anche col server a
// true (stessa classe di bug T-302 warehouse/collettiva).
function readCachedRecipeQtyFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(RECIPE_QTY_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedRecipeQtyFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(RECIPE_QTY_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}

/**
 * Per-tenant `recipe_quantities_enabled` con fallback localStorage (offline-first).
 * Default false = ricette gestite senza dosi (qty/uom nascoste nell'editor).
 */
export function useRecipeQuantitiesEnabled(): boolean {
  // T-130 (invariante I1): col MAGAZZINO attivo le quantità ricette sono
  // OBBLIGATORIE → sempre attive, a prescindere dal flag memorizzato. Un trigger
  // DB forza già `recipe_quantities_enabled=true` quando `warehouse_enabled=true`,
  // ma la cache client può essere stantia: derivandolo dal magazzino il flag non
  // può più "spegnersi" per errore quando il magazzino è ON.
  const warehouse = useWarehouseEnabled();
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedRecipeQtyFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_recipe_quantities_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    initialDataUpdatedAt: cached === undefined ? undefined : 0,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('recipe_quantities_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { recipe_quantities_enabled: boolean } | null)?.recipe_quantities_enabled;
      writeCachedRecipeQtyFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return warehouse || (query.data ?? cached ?? false);
}

export function useSetRecipeQuantitiesEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase()
        .from('tenants')
        .update({ recipe_quantities_enabled: enabled })
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedRecipeQtyFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_recipe_quantities_enabled'] });
    },
  });
}

const COLLETTIVA_FLAG_CACHE_PREFIX = 'niveo.tenant_collettiva_enabled.';

// Vedi readCachedWarehouseFlag: undefined su miss ⇒ fetch immediata (no flag
// OFF per 5 min al primo login su device nuovo).
function readCachedCollettivaFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(COLLETTIVA_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedCollettivaFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(COLLETTIVA_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}

/**
 * T-215: per-tenant `collettiva_enabled` flag (modulo ristorazione collettiva:
 * scheda di produzione giornaliera, DDT in uscita, trasferimenti interni,
 * destinazioni). Stesso pattern di useWarehouseEnabled: localStorage fallback
 * per cold start/offline, refresh dal server quando online. Default false.
 */
export function useCollettivaEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedCollettivaFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_collettiva_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('collettiva_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { collettiva_enabled: boolean } | null)?.collettiva_enabled;
      writeCachedCollettivaFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

export function useSetCollettivaEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase()
        .from('tenants')
        .update({ collettiva_enabled: enabled })
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedCollettivaFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_collettiva_enabled'] });
    },
  });
}

// ───────── T-291: flag `receiving_temp_per_item` ─────────
// Quando ON, il Carico DDT/fattura chiede la temperatura di ricevimento PER
// ogni articolo (oltre alla conformità generale del documento). Il valore va
// nel payload `conformity_check.item_temps[{food_id,temp_c}]` (append-only) ed
// è già letto per riga dall'export MOD 01 (export-carico-month). Default false =
// comportamento attuale (solo flag conformità generale). Stesso pattern flag.
const RECEIVING_TEMP_FLAG_CACHE_PREFIX = 'niveo.tenant_receiving_temp_per_item.';

function readCachedReceivingTempFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(RECEIVING_TEMP_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedReceivingTempFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(RECEIVING_TEMP_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}

export function useReceivingTempPerItemEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedReceivingTempFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_receiving_temp_per_item', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('receiving_temp_per_item')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { receiving_temp_per_item: boolean } | null)?.receiving_temp_per_item;
      writeCachedReceivingTempFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

export function useSetReceivingTempPerItemEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase()
        .from('tenants')
        .update({ receiving_temp_per_item: enabled })
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedReceivingTempFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_receiving_temp_per_item'] });
    },
  });
}

// ───────── T-450 F1: aliquota IVA di default del locale (Food Cost) ─────────
// Persistita in `tenants.settings.default_vat_rate` (JSONB, accanto a
// `collettiva_prelabel_list`) — nessuna colonna/tabella nuova. È l'aliquota % con
// cui il Food Cost scorpora l'IVA dal prezzo di vendita (che a menù è LORDO): una
// ricetta può fare override con `recipes.vat_rate`. Default 10 (ristorazione).
// Stesso pattern read-modify-write del prelabel (preserva le altre chiavi di
// settings) + cache localStorage per il cold-start.
const DEFAULT_VAT_RATE = 10;
const VAT_RATE_CACHE_PREFIX = 'niveo.tenant_default_vat_rate.';

function readCachedVatRate(tenantId: string | null): number | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(VAT_RATE_CACHE_PREFIX + tenantId);
    if (raw == null) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedVatRate(tenantId: string, rate: number): void {
  try {
    localStorage.setItem(VAT_RATE_CACHE_PREFIX + tenantId, String(rate));
  } catch { /* noop */ }
}

/** Aliquota IVA di default del locale (%). Default 10 se non impostata. */
export function useDefaultVatRate(): number {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedVatRate(tenantId);
  const query = useQuery<number>({
    queryKey: ['tenant_default_vat_rate', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('settings')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const raw = (data as { settings?: { default_vat_rate?: unknown } } | null)?.settings?.default_vat_rate;
      const n = Number(raw);
      const rate = Number.isFinite(n) && n >= 0 ? n : DEFAULT_VAT_RATE;
      writeCachedVatRate(tenantId!, rate);
      return rate;
    },
  });
  return query.data ?? cached ?? DEFAULT_VAT_RATE;
}

export function useSetDefaultVatRate() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (rate: number): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      if (!Number.isFinite(rate) || rate < 0) throw new Error('Aliquota IVA non valida');
      const sb = getSupabase();
      // Read-modify-write: preserva le altre chiavi di settings.
      const { data: cur, error: readErr } = await sb
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();
      if (readErr) throw readErr;
      const settings = {
        ...((cur as { settings?: Record<string, unknown> } | null)?.settings ?? {}),
        default_vat_rate: rate,
      };
      const { error } = await sb.from('tenants').update({ settings }).eq('id', tenantId);
      if (error) throw error;
      writeCachedVatRate(tenantId, rate);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_default_vat_rate'] });
    },
  });
}

// ───────── T-303: lista alimenti pre-stampa collettiva (Bluecoop) ─────────
// Persistita in `tenants.settings.collettiva_prelabel_list` (JSONB), accanto a
// `billing_memo` — nessuna tabella nuova. Letta con cache localStorage (stesso
// pattern dei flag modulo), scritta online con read-modify-write per non
// sovrascrivere le altre chiavi di `settings`.
const PRELABEL_CACHE_PREFIX = 'niveo.tenant_collettiva_prelabel.';

// undefined su miss ⇒ fetch immediata (vedi nota cache-miss dei flag modulo).
function readCachedPrelabel(tenantId: string | null): CollettivaPrelabelRow[] | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(PRELABEL_CACHE_PREFIX + tenantId);
    if (!raw) return undefined;
    const parsed = CollettivaPrelabelListSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedPrelabel(tenantId: string, rows: CollettivaPrelabelRow[]): void {
  try {
    localStorage.setItem(PRELABEL_CACHE_PREFIX + tenantId, JSON.stringify(rows));
  } catch { /* noop */ }
}

export function useCollettivaPrelabelList(enabled = true): CollettivaPrelabelRow[] {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedPrelabel(tenantId);
  const query = useQuery<CollettivaPrelabelRow[]>({
    queryKey: ['tenant_collettiva_prelabel', tenantId],
    enabled: !!tenantId && enabled,
    initialData: cached,
    // T-406 FIX: la cache localStorage serviva solo per il cold-start; con
    // staleTime 5min la lista modificata (ricette/categorie) restava vecchia
    // per minuti. Ora si ricarica SEMPRE all'apertura del tab (la cache resta
    // solo come primo render istantaneo, poi arriva subito il fresco).
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('settings')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const raw = (data as { settings?: { collettiva_prelabel_list?: unknown } } | null)
        ?.settings?.collettiva_prelabel_list;
      const parsed = CollettivaPrelabelListSchema.safeParse(raw ?? []);
      const rows = (parsed.success ? parsed.data : []).slice().sort((a, b) => a.sort_order - b.sort_order);
      writeCachedPrelabel(tenantId!, rows);
      return rows;
    },
  });
  return query.data ?? cached ?? [];
}

export function useSetCollettivaPrelabelList() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (rows: CollettivaPrelabelRow[]): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      // Valida + normalizza sort_order all'indice dell'array (l'ordine = la lista).
      const clean = CollettivaPrelabelListSchema.parse(
        rows.map((r, i) => ({ ...r, sort_order: i })),
      );
      const sb = getSupabase();
      // Read-modify-write: preserviamo le altre chiavi di settings (es. billing_memo).
      const { data: cur, error: readErr } = await sb
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();
      if (readErr) throw readErr;
      const settings = {
        ...((cur as { settings?: Record<string, unknown> } | null)?.settings ?? {}),
        collettiva_prelabel_list: clean,
      };
      const { error } = await sb.from('tenants').update({ settings }).eq('id', tenantId);
      if (error) throw error;
      writeCachedPrelabel(tenantId, clean);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_collettiva_prelabel'] });
    },
  });
}

// ───────── T-347: bozza scarico merce collettiva (Bluecoop) ─────────
// Persistita in `tenants.settings.collettiva_discharge` come mappa
// `{ "<biz>_<data>_<meal>": draft }`. Lo chef SALVA la bozza (lotti+qtà), il
// responsabile la rilegge e SCARICA & CHIUDE. Nessuna tabella nuova; sincronizza
// via master `tenants`. Read-modify-write per non toccare le altre chiavi di
// settings. Pruning: teniamo solo le bozze degli ultimi 21 giorni (chiave contiene
// la data ISO) per non gonfiare settings.
export function collettivaDischargeKey(businessId: string, date: string, meal: string): string {
  return `${businessId}_${date}_${meal}`;
}

function pruneDischargeMap(map: Record<string, CollettivaDischargeDraft>): Record<string, CollettivaDischargeDraft> {
  // La chiave è `<uuid>_<YYYY-MM-DD>_<meal>`: estrai la data (penultimo segmento).
  const cutoff = Date.now() - 21 * 24 * 60 * 60 * 1000;
  const out: Record<string, CollettivaDischargeDraft> = {};
  for (const [k, v] of Object.entries(map)) {
    const parts = k.split('_');
    const dateStr = parts.length >= 3 ? parts[parts.length - 2] : null;
    const t = dateStr ? Date.parse(dateStr) : NaN;
    if (!Number.isFinite(t) || t >= cutoff) out[k] = v;
  }
  return out;
}

export function useCollettivaDischargeDraft(key: string | null): CollettivaDischargeDraft | null {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const query = useQuery<CollettivaDischargeDraft | null>({
    queryKey: ['tenant_collettiva_discharge', tenantId, key],
    enabled: !!tenantId && !!key,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('settings')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const raw = (data as { settings?: { collettiva_discharge?: Record<string, unknown> } } | null)
        ?.settings?.collettiva_discharge?.[key!];
      if (raw == null) return null;
      const parsed = CollettivaDischargeDraftSchema.safeParse(raw);
      return parsed.success ? parsed.data : null;
    },
  });
  return query.data ?? null;
}

export function useSetCollettivaDischargeDraft() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { key: string; draft: CollettivaDischargeDraft }): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const clean = CollettivaDischargeDraftSchema.parse(input.draft);
      const sb = getSupabase();
      const { data: cur, error: readErr } = await sb
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();
      if (readErr) throw readErr;
      const settings = { ...((cur as { settings?: Record<string, unknown> } | null)?.settings ?? {}) };
      const existingMap = (settings.collettiva_discharge as Record<string, CollettivaDischargeDraft> | undefined) ?? {};
      settings.collettiva_discharge = pruneDischargeMap({ ...existingMap, [input.key]: clean });
      const { error } = await sb.from('tenants').update({ settings }).eq('id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_collettiva_discharge'] });
    },
  });
}

const FOOD_COST_FLAG_CACHE_PREFIX = 'niveo.tenant_food_cost_enabled.';
// Il valore locale serve solo a rendere l'avvio offline immediato. Un modulo
// commerciale può essere attivato da un altro device mentre questo resta aperto:
// non deve quindi diventare una cache autorevole per minuti o fino al reset.
const FOOD_COST_FLAG_REFETCH_INTERVAL_MS = 60_000;

// Vedi readCachedCollettivaFlag: undefined su miss ⇒ fetch immediata (no flag
// OFF per la durata dello staleTime al primo login su device nuovo).
function readCachedFoodCostFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(FOOD_COST_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedFoodCostFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(FOOD_COST_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}

/**
 * T-285: per-tenant `food_cost_enabled` flag (modulo Food Cost: costo ricette,
 * food cost %, report). Stesso pattern di useCollettivaEnabled: lettura on-demand
 * (NON nel JWT), localStorage fallback per cold start/offline. Default false.
 */
export function useFoodCostEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedFoodCostFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_food_cost_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 0,
    refetchInterval: FOOD_COST_FLAG_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('food_cost_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { food_cost_enabled: boolean } | null)?.food_cost_enabled;
      writeCachedFoodCostFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

/** Gate autoritativo per i deep-link al Food Cost: niente cache locale e
 * nessuna autorizzazione implicita mentre la lettura server è in corso. */
export function useFoodCostAccessGate(): { enabled: boolean; checking: boolean; error: boolean } {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const attemptId = useRef(`${Date.now()}-${Math.random()}`).current;
  const query = useQuery<boolean>({
    queryKey: ['tenant_food_cost_access', tenantId, attemptId],
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('food_cost_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      return !!(data as { food_cost_enabled: boolean } | null)?.food_cost_enabled;
    },
  });
  return {
    enabled: query.isSuccess && query.data === true,
    checking: !!tenantId && (query.isPending || query.isFetching),
    error: !tenantId || query.isError,
  };
}

export function useSetFoodCostEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase()
        .from('tenants')
        .update({ food_cost_enabled: enabled })
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedFoodCostFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_food_cost_enabled'] });
      void qc.invalidateQueries({ queryKey: ['tenant_food_cost_access'] });
    },
  });
}

// T-473: capability indipendente per i preventivi eventi e sale. Mantiene il
// pattern offline-first dei flag modulo: cache per tenant + fetch on-demand.
const EVENT_QUOTES_FLAG_CACHE_PREFIX = 'niveo.tenant_event_quotes_enabled.';
function readCachedEventQuotesFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(EVENT_QUOTES_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch { return undefined; }
}
function writeCachedEventQuotesFlag(tenantId: string, enabled: boolean): void {
  try { localStorage.setItem(EVENT_QUOTES_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0'); } catch { /* noop */ }
}
export function useEventQuotesEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedEventQuotesFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_event_quotes_enabled', tenantId], enabled: !!tenantId,
    initialData: cached, staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase().from('tenants').select('event_quotes_enabled').eq('id', tenantId!).single();
      if (error) throw error;
      const enabled = !!(data as { event_quotes_enabled: boolean } | null)?.event_quotes_enabled;
      writeCachedEventQuotesFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

/**
 * Gate autoritativo per l'ingresso alla feature a pagamento. A differenza del
 * tile offline-first, non autorizza mai da localStorage o da una query stale:
 * ogni mount della pagina esegue una lettura server nuova e fallisce chiuso.
 */
export function useEventQuotesAccessGate(): { enabled: boolean; checking: boolean; error: boolean } {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const attemptId = useRef(`${Date.now()}-${Math.random()}`).current;
  const query = useQuery<boolean>({
    queryKey: ['tenant_event_quotes_access', tenantId, attemptId],
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('event_quotes_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      return !!(data as { event_quotes_enabled: boolean } | null)?.event_quotes_enabled;
    },
  });
  return {
    enabled: query.isSuccess && query.data === true,
    checking: !!tenantId && (query.isPending || query.isFetching),
    error: !tenantId || query.isError,
  };
}

export function useSetEventQuotesEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase().from('tenants').update({ event_quotes_enabled: enabled }).eq('id', tenantId);
      if (error) throw error;
      writeCachedEventQuotesFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_event_quotes_enabled'] });
      void qc.invalidateQueries({ queryKey: ['tenant_event_quotes_access'] });
    },
  });
}

// T-562: acquisizione fotografica dei lotti durante produzione/ricetta.
// La cache è separata per tenant e accetta solo i due valori serializzati noti;
// cache assente o corrotta = feature spenta fino a una risposta autoritativa.
const PRODUCTION_LOT_PHOTO_FLAG_CACHE_PREFIX =
  'niveo.tenant_production_lot_photo_enabled.';

function readCachedProductionLotPhotoFlag(
  tenantId: string | null,
): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(PRODUCTION_LOT_PHOTO_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedProductionLotPhotoFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(
      PRODUCTION_LOT_PHOTO_FLAG_CACHE_PREFIX + tenantId,
      enabled ? '1' : '0',
    );
  } catch { /* noop */ }
}

/**
 * Offline-first tenant flag. A cached snapshot is usable offline but is marked
 * stale immediately, so every mount/reconnect asks the server again. Cold or
 * malformed cache state fails closed to false.
 */
export function useProductionLotPhotoEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedProductionLotPhotoFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_production_lot_photo_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    initialDataUpdatedAt: cached === undefined ? undefined : 0,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('production_lot_photo_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = (
        data as { production_lot_photo_enabled?: boolean } | null
      )?.production_lot_photo_enabled === true;
      writeCachedProductionLotPhotoFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

// T-563: modulo autonomo per DDT in uscita e trasferimenti dei ristoranti.
// La tile usa la cache offline; le pagine operative usano il gate server-fresh
// sottostante per non autorizzare mai da un valore locale stantio.
const TRANSPORT_DOCUMENTS_FLAG_CACHE_PREFIX = 'niveo.tenant_transport_documents_enabled.';
function readCachedTransportDocumentsFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(TRANSPORT_DOCUMENTS_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch { return undefined; }
}
function writeCachedTransportDocumentsFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(TRANSPORT_DOCUMENTS_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}
export function useTransportDocumentsEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedTransportDocumentsFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_transport_documents_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('transport_documents_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { transport_documents_enabled: boolean } | null)?.transport_documents_enabled;
      writeCachedTransportDocumentsFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

export function useSetProductionLotPhotoEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<{ tenantId: string; enabled: boolean }> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { data, error } = await getSupabase().rpc(
        'set_production_lot_photo_enabled_v1',
        { p_enabled: enabled },
      );
      if (error) throw error;
      if (data !== enabled) {
        throw new Error('Risposta non valida durante il salvataggio della funzione foto lotti');
      }
      writeCachedProductionLotPhotoFlag(tenantId, data);
      return { tenantId, enabled: data };
    },
    onSuccess: ({ tenantId, enabled }) => {
      qc.setQueryData(['tenant_production_lot_photo_enabled', tenantId], enabled);
      void qc.invalidateQueries({
        queryKey: ['tenant_production_lot_photo_enabled', tenantId],
      });
    },
  });
}

export function useTransportDocumentsAccessGate(): {
  enabled: boolean;
  checking: boolean;
  error: boolean;
} {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const attemptId = useRef(`${Date.now()}-${Math.random()}`).current;
  const query = useQuery<boolean>({
    queryKey: ['tenant_transport_documents_access', tenantId, attemptId],
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('transport_documents_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      return !!(data as { transport_documents_enabled: boolean } | null)?.transport_documents_enabled;
    },
  });
  // Le IonPage restano montate nella cache del router: ogni ingresso deve
  // riverificare il flag. Dopo il primo successo il contenuto resta montato
  // durante il refetch; un risultato false o errore lo richiude subito.
  useIonViewWillEnter(() => {
    if (tenantId) void query.refetch();
  }, [tenantId, query.refetch]);
  return resolveTransportDocumentsAccessState({
    hasTenant: !!tenantId,
    data: query.data,
    isSuccess: query.isSuccess,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
  });
}

export function useSetTransportDocumentsEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase()
        .from('tenants')
        .update({ transport_documents_enabled: enabled })
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedTransportDocumentsFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_transport_documents_enabled'] });
      void qc.invalidateQueries({ queryKey: ['tenant_transport_documents_access'] });
    },
  });
}

// T-373: modulo "Agente AI" (placeholder). Stesso pattern di useFoodCostEnabled:
// lettura on-demand (NON nel JWT) + localStorage fallback. Default false. La
// feature vera (assistente conversazionale tenant-scoped) è T-374.
const AI_AGENT_FLAG_CACHE_PREFIX = 'niveo.tenant_ai_agent_enabled.';

function readCachedAiAgentFlag(tenantId: string | null): boolean | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(AI_AGENT_FLAG_CACHE_PREFIX + tenantId);
    return raw === '1' ? true : raw === '0' ? false : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedAiAgentFlag(tenantId: string, enabled: boolean): void {
  try {
    localStorage.setItem(AI_AGENT_FLAG_CACHE_PREFIX + tenantId, enabled ? '1' : '0');
  } catch { /* noop */ }
}

export function useAiAgentEnabled(): boolean {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedAiAgentFlag(tenantId);
  const query = useQuery<boolean>({
    queryKey: ['tenant_ai_agent_enabled', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('ai_agent_enabled')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      const enabled = !!(data as { ai_agent_enabled: boolean } | null)?.ai_agent_enabled;
      writeCachedAiAgentFlag(tenantId!, enabled);
      return enabled;
    },
  });
  return query.data ?? cached ?? false;
}

export function useSetAiAgentEnabled() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { error } = await getSupabase()
        .from('tenants')
        .update({ ai_agent_enabled: enabled })
        .eq('id', tenantId);
      if (error) throw error;
      writeCachedAiAgentFlag(tenantId, enabled);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_ai_agent_enabled'] });
    },
  });
}

// T-227: configurazione per-ruolo della visibilità dei tile della Dashboard.
// Forma { "<ruolo>": { "<tileKey>": false } } — solo gli OFF sono memorizzati.
export type DashboardConfig = Record<string, Record<string, boolean>>;

const DASHBOARD_CFG_CACHE_PREFIX = 'niveo.tenant_dashboard_config.';

// Ritorna `undefined` su cache-miss (NON `{}`): un `{}` passato come
// `initialData` con `staleTime>0` farebbe credere a React Query di avere già
// la config "fresca" → niente fetch dal server per tutta la staleTime, e per
// quel periodo TUTTI i tile risultano visibili (config vuota = nessun tile
// nascosto). Con `undefined` la query parte subito. Stesso fix dei flag
// warehouse/collettiva (vedi `readCachedWarehouseFlag`).
function readCachedDashboardConfig(tenantId: string | null): DashboardConfig | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(DASHBOARD_CFG_CACHE_PREFIX + tenantId);
    return raw ? (JSON.parse(raw) as DashboardConfig) : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedDashboardConfig(tenantId: string, cfg: DashboardConfig): void {
  try {
    localStorage.setItem(DASHBOARD_CFG_CACHE_PREFIX + tenantId, JSON.stringify(cfg));
  } catch { /* noop */ }
}

/**
 * T-227: legge `tenants.dashboard_config` (per-ruolo) con fallback localStorage
 * per cold start/offline. Default {} = tutti i tile visibili.
 */
export function useDashboardConfig(): DashboardConfig {
  return useDashboardConfigState().config;
}

export function useDashboardConfigState(): {
  config: DashboardConfig;
  resolved: boolean;
  refreshing: boolean;
  unavailable: boolean;
} {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const cached = readCachedDashboardConfig(tenantId);
  const query = useQuery<DashboardConfig>({
    queryKey: ['tenant_dashboard_config', tenantId],
    enabled: !!tenantId,
    initialData: cached,
    // Il localStorage è solo uno snapshot offline, non prova di freschezza.
    // `0` forza la riconciliazione server al mount anche quando una cache esiste.
    initialDataUpdatedAt: cached === undefined ? undefined : 0,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
    // Copre il caso due dispositivi entrambi già aperti: il salvataggio della
    // PWA raggiunge l'APK senza richiedere logout o riavvio.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('dashboard_config')
        .eq('id', tenantId!)
        .single()
        .retry(false);
      if (error) throw error;
      const cfg = ((data as { dashboard_config?: DashboardConfig } | null)?.dashboard_config ?? {}) as DashboardConfig;
      writeCachedDashboardConfig(tenantId!, cfg);
      return cfg;
    },
  });
  const serverResolved = query.dataUpdatedAt > 0;
  const offlineSnapshotResolved = cached !== undefined
    && (!navigator.onLine || query.isError);
  return {
    config: query.data ?? cached ?? {},
    // Senza server e senza snapshot locale non conosciamo i permessi: le route
    // configurabili restano fail-closed. Con una cache, l'app resta offline-first.
    resolved: !!tenantId && (serverResolved || offlineSnapshotResolved),
    refreshing: query.isFetching,
    unavailable: !!tenantId && query.isError && !offlineSnapshotResolved,
  };
}

export function useSetDashboardConfig() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { expected: DashboardConfig; next: DashboardConfig }): Promise<DashboardConfig> => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const { data, error } = await getSupabase().rpc('set_tenant_dashboard_config_v1', {
        p_expected: input.expected,
        p_next: input.next,
      });
      if (error) throw error;
      const saved = (data ?? {}) as DashboardConfig;
      writeCachedDashboardConfig(tenantId, saved);
      qc.setQueryData(['tenant_dashboard_config', tenantId], saved);
      return saved;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant_dashboard_config'] });
    },
  });
}
